import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { canUseDOM } from '@ps/web-core-utils';
import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import Loadable from 'react-loadable';

interface Styles {
  theme: string;
  darkTheme: string;
}

export interface Props {
  onThemeChange: (theme: string, darkTheme?: string) => void;
}

const Theme = ({ theme, darkTheme, onThemeChange }: Styles & Props) => {
  useEffect(() => {
    onThemeChange(theme, darkTheme);
  }, [theme, darkTheme, onThemeChange]);

  // Ensure theme is set on SSR render
  if (!canUseDOM) {
    return <BodyClassName className={theme} />;
  }

  return null;
};

const render = ({ theme, darkTheme }: Styles, { onThemeChange }: Props) => (
  <Theme theme={theme} darkTheme={darkTheme} onThemeChange={onThemeChange} />
);

const loading = () => null;

const defaultTheme = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './defaultTheme.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const fullTilt = Loadable({
  loader: () =>
    import(/* webpackChunkName: "FullTilt" */ './fullTilt.scss').then((promise) => promise.default),
  modules: ['./FullTilt'],
  loading,
  render
});

const pokerStars = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerStars.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const sport = Loadable({
  loader: () =>
    import(/* webpackChunkName: "Sports" */ './sports.scss').then((promise) => promise.default),
  modules: ['./Sports'],
  loading,
  render
});

const starsCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './starsCasino.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const multiBrandCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './multiBrandCasino.scss').then(
      (promise) => promise.default
    ),
  modules: ['./CMS'],
  loading,
  render
});

const multiBrandPoker = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './multiBrandPoker.scss').then(
      (promise) => promise.default
    ),
  modules: ['./CMS'],
  loading,
  render
});

const pokerDotNet = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerDotNet.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const deshPoker = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './deshPoker.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const deshCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './deshCasino.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const pokerDotNetDE = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerDotNetDE.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const foxBet = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './foxBet.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const sisalIT = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './sisalIT.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

export type ThemeType = typeof defaultTheme;

export const themes = {
  none: null,
  defaultTheme,
  starsCasino,
  pokerStars,
  fullTilt,
  sport,
  deshCasino,
  deshPoker,
  pokerDotNet,
  pokerDotNetDE,
  multiBrandCasino,
  multiBrandPoker,
  foxBet,
  sisalIT
};

export type GetTheme = ({
  isSiteUS,
  brand,
  site,
  region
}: {
  isSiteUS: boolean;
  brand: BrandTypes;
  site: Sites;
  region?: string;
}) => React.ComponentType<Props> & Loadable.LoadableComponent;

// TODO: This can be a hook when SCSS import is not component based and `getStyle` returns CSS className not a component
export const getTheme: GetTheme = ({ isSiteUS, brand, site, region }) => {
  switch (true) {
    case site === Sites.IT_SISAL_EXT:
      return sisalIT;
    case site === Sites.DE_SH:
      return brand === BrandTypes.STARS_CASINO ? deshCasino : deshPoker;
    case isSiteUS && brand === BrandTypes.STARS_CASINO:
      return multiBrandCasino;
    case isSiteUS && brand === BrandTypes.POKER_STARS:
      return multiBrandPoker;
    case site === Sites.NET:
      return region === 'DE' ? pokerDotNetDE : pokerDotNet;
    case brand === BrandTypes.BET_STARS:
      return sport;
    case brand === BrandTypes.FULL_TILT:
      return fullTilt;
    case brand === BrandTypes.STARS_CASINO:
      return starsCasino;
    case brand === BrandTypes.POKER_STARS:
      return pokerStars;
    case isSiteUS && brand === BrandTypes.FOX_BET:
      return foxBet;
    default:
      return defaultTheme;
  }
};
